import { useEffect, useState } from "react";
import "./Admin.css";
import { login, authedFetch, authedPost, getAdminToken, clearAdminToken, API_URL } from "./api";

function formatAgo(ms) {
  if (ms == null || ms < 0) return "just now";
  const s = Math.floor(ms / 1000);
  if (s < 10) return "just now";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h`;
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [passcodeInput, setPasscodeInput] = useState("");
  const [passError, setPassError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [submissions, setSubmissions] = useState([]);
  const [pendingParties, setPendingParties] = useState([]);
  const [inviteStats, setInviteStats] = useState({ total_invited_parties: 0, total_invited_headcount: 0, pending_headcount: 0 });
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | attending | declined | pending
  const [expandedId, setExpandedId] = useState(null);

  const [lastUpdated, setLastUpdated] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());

  // Verify any existing admin token on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAdminToken()) {
        if (!cancelled) { setAuthed(false); setCheckingAuth(false); }
        return;
      }
      try {
        const res = await authedFetch("admin", "/api/auth/me");
        if (!cancelled) {
          if (res.ok) setAuthed(true);
          else { clearAdminToken(); setAuthed(false); }
          setCheckingAuth(false);
        }
      } catch {
        if (!cancelled) { clearAdminToken(); setAuthed(false); setCheckingAuth(false); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authed) return;
    fetchSubmissions();
    fetchPending();
  }, [authed]);

  // Live refresh: refetch when the tab regains focus, when the browser reports
  // visibility changes, and every 30s while the tab is active. This ensures
  // new RSVPs submitted by guests on other devices reflect on the dashboard
  // without the couple having to click Refresh.
  useEffect(() => {
    if (!authed) return;
    const onFocus = () => { fetchSubmissions(); fetchPending(); };
    const onVisibility = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const pollId = setInterval(() => {
      if (document.visibilityState === "visible") onFocus();
    }, 30000);
    // "X min ago" ticker
    const tickId = setInterval(() => setNowTs(Date.now()), 15000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(pollId);
      clearInterval(tickId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handlePasscodeSubmit = async (e) => {
    e.preventDefault();
    if (loggingIn) return;
    setLoggingIn(true);
    setPassError("");
    const { ok, error } = await login("admin", passcodeInput.trim());
    setLoggingIn(false);
    if (ok) {
      setAuthed(true);
      setPasscodeInput("");
    } else {
      setPassError(error || "Incorrect code. Please try again.");
      setPasscodeInput("");
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    setAuthed(false);
    setPasscodeInput("");
  };

  const requireAuthOr401 = (res) => {
    if (res.status === 401) {
      clearAdminToken();
      setAuthed(false);
      return false;
    }
    return true;
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await authedFetch('admin', `/api/rsvp/submissions?skip=0&limit=1000`);
      if (!requireAuthOr401(res)) return;
      const data = await res.json();
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
      setLastUpdated(Date.now());
    } catch (err) {
      console.error('Failed to load submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPending = async () => {
    try {
      const res = await authedFetch('admin', `/api/rsvp/non-responders`);
      if (!requireAuthOr401(res)) return;
      const data = await res.json();
      setPendingParties(data.parties || []);
      setInviteStats({
        total_invited_parties: data.total_invited_parties || 0,
        total_invited_headcount: data.total_invited_headcount || 0,
      });
    } catch (err) {
      console.error('Failed to load non-responders:', err);
    }
  };

  // ---------- SMS reminder helpers ----------
  const [phoneDrafts, setPhoneDrafts] = useState({}); // { [guest_id]: "raw input" }
  const [phoneSaving, setPhoneSaving] = useState({}); // { [guest_id]: bool }

  const savePhone = async (guestId, phone) => {
    setPhoneSaving(prev => ({ ...prev, [guestId]: true }));
    try {
      const res = await authedPost('admin', '/api/rsvp/set-phone', { guest_id: guestId, phone });
      if (!requireAuthOr401(res)) return;
      if (res.ok) {
        setPendingParties(prev => prev.map(p => p.id === guestId ? { ...p, phone } : p));
        setPhoneDrafts(prev => { const n = { ...prev }; delete n[guestId]; return n; });
      }
    } catch (err) {
      console.error('save phone failed', err);
    } finally {
      setPhoneSaving(prev => ({ ...prev, [guestId]: false }));
    }
  };

  const buildReminderText = (party) => {
    const firstName = (party.first_name || party.primary_guest || '').split(' ')[0];
    const greeting = firstName ? `Hi ${firstName}!` : `Hi!`;
    return `${greeting} 💌 This is Nathan & Sandra with a friendly reminder to save the date & RSVP for our wedding on Nov 7, 2026 at normansforever.com. Can't wait to celebrate with you!`;
  };

  const smsHref = (phone, party) => {
    const cleaned = (phone || '').replace(/[^0-9+]/g, '');
    const body = encodeURIComponent(buildReminderText(party));
    // Universal sms link that works on iOS + Android. Use `?&body=` for iOS compat.
    return `sms:${cleaned}?&body=${body}`;
  };

  const refreshAll = () => {
    fetchSubmissions();
    fetchPending();
  };

  // Return the ceremony/cocktail/reception flag for a guest, with legacy fallback
  const attendsCeremony = (g) =>
    typeof g.attending_ceremony === 'boolean' ? g.attending_ceremony : !!g.attending;
  const attendsCocktail = (g) =>
    typeof g.attending_cocktail === 'boolean' ? g.attending_cocktail : !!g.attending;
  const attendsReception = (g) =>
    typeof g.attending_reception === 'boolean' ? g.attending_reception : !!g.attending;

  // Calculate stats
  const stats = submissions.reduce((acc, sub) => {
    const guests = sub.guests || [];
    const attendingCount = guests.filter(g => g.attending).length;
    const decliningCount = guests.filter(g => !g.attending).length;
    acc.totalRsvps += 1;
    acc.attending += attendingCount;
    acc.declining += decliningCount;
    acc.ceremony += guests.filter(attendsCeremony).length;
    acc.cocktail += guests.filter(attendsCocktail).length;
    acc.reception += guests.filter(attendsReception).length;
    if (attendingCount > 0) acc.familiesAttending += 1;
    return acc;
  }, { totalRsvps: 0, attending: 0, declining: 0, ceremony: 0, cocktail: 0, reception: 0, familiesAttending: 0 });

  // Filter
  const filtered = submissions.filter(sub => {
    const matchesSearch = !search || 
      (sub.primary_guest || '').toLowerCase().includes(search.toLowerCase()) ||
      (sub.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (sub.family || '').toLowerCase().includes(search.toLowerCase());
    
    const guests = sub.guests || [];
    const anyAttending = guests.some(g => g.attending);
    const anyDeclining = guests.some(g => !g.attending);
    const matchesFilter = filter === 'all' ||
      (filter === 'attending' && anyAttending) ||
      (filter === 'declined' && anyDeclining);
    
    return matchesSearch && matchesFilter;
  });

  const exportCSV = () => {
    const headers = ['Submitted', 'Family', 'Primary Guest', 'Email', 'Ceremony (2pm) Yes', 'Ceremony No', 'Cocktail (2:45pm) Yes', 'Cocktail No', 'Reception (4pm) Yes', 'Reception No', 'Guests Detail', 'Dietary Notes', 'Song Request', 'Message'];
    const rows = submissions.map(sub => {
      const guests = sub.guests || [];
      const ceremonyYes = guests.filter(attendsCeremony).length;
      const ceremonyNo = guests.length - ceremonyYes;
      const cocktailYes = guests.filter(attendsCocktail).length;
      const cocktailNo = guests.length - cocktailYes;
      const receptionYes = guests.filter(attendsReception).length;
      const receptionNo = guests.length - receptionYes;
      const detail = guests.map(g =>
        `${g.name}: Ceremony=${attendsCeremony(g) ? 'Yes' : 'No'}, Cocktail=${attendsCocktail(g) ? 'Yes' : 'No'}, Reception=${attendsReception(g) ? 'Yes' : 'No'}`
      ).join('; ');
      return [
        sub.submitted_at || '',
        sub.family || '',
        sub.primary_guest || '',
        sub.email || '',
        ceremonyYes,
        ceremonyNo,
        cocktailYes,
        cocktailNo,
        receptionYes,
        receptionNo,
        detail,
        sub.dietary_notes || '',
        sub.song_request || '',
        sub.message || '',
      ];
    });
    
    const csv = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wedding-rsvps-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const exportExcel = () => {
    const token = getAdminToken();
    if (!token) { clearAdminToken(); setAuthed(false); return; }
    window.location.href = `${API_URL}/api/rsvp/export-excel?token=${encodeURIComponent(token)}`;
  };

  const copyNonResponders = async (format = 'first') => {
    try {
      const res = await authedFetch('admin', `/api/rsvp/non-responders`);
      if (!requireAuthOr401(res)) return;
      const data = await res.json();
      const parties = data.parties || [];
      if (parties.length === 0) {
        alert('🎉 Everyone has RSVP\'d!');
        return;
      }
      const names = parties.map(p =>
        format === 'first' ? (p.first_name || p.primary_guest) : p.primary_guest
      );
      const text = names.join(', ');
      await navigator.clipboard.writeText(text);
      alert(`✓ Copied ${parties.length} non-responder${parties.length === 1 ? '' : 's'} to clipboard:\n\n${text.slice(0, 200)}${text.length > 200 ? '…' : ''}`);
    } catch (err) {
      console.error(err);
      alert('Could not copy to clipboard. Check console for details.');
    }
  };

  const exportAndWipe = async () => {
    const exported = window.confirm(
      "🔒 PERMANENTLY DELETE ALL RSVP DATA\n\n" +
      "Before continuing, have you already downloaded the FULL Excel export and saved a backup somewhere safe?\n\n" +
      "Click OK only if you have a backup. Click Cancel to download Excel first."
    );
    if (!exported) {
      exportExcel();
      return;
    }
    const phrase = window.prompt(
      "⚠️ FINAL CONFIRMATION\n\n" +
      "This will PERMANENTLY DELETE every RSVP submission your guests have made. This cannot be undone.\n\n" +
      "To proceed, type exactly:\n" +
      "DELETE ALL DATA"
    );
    if (phrase !== "DELETE ALL DATA") {
      alert("Cancelled. No data was deleted.");
      return;
    }
    try {
      const res = await authedPost('admin', `/api/rsvp/wipe-all-data`, { confirmation: phrase });
      if (!requireAuthOr401(res)) return;
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Wipe failed');
      alert(`✓ All RSVP data permanently deleted.\n\nSubmissions removed: ${data.submissions_deleted}\nVerification codes removed: ${data.codes_deleted}`);
      refreshAll();
    } catch (err) {
      console.error(err);
      alert(`Could not wipe data: ${err.message}`);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="admin-page">
      {checkingAuth ? (
        <div className="admin-gate" data-testid="admin-gate-loading">
          <div className="admin-gate-card"><p className="admin-gate-sub">Loading…</p></div>
        </div>
      ) : !authed ? (
        <div className="admin-gate" data-testid="admin-gate">
          <div className="admin-gate-card">
            <div className="admin-gate-eyebrow">Private</div>
            <h1 className="admin-gate-title">Wedding Admin</h1>
            <p className="admin-gate-sub">Please enter the access code to continue.</p>
            <form onSubmit={handlePasscodeSubmit} className="admin-gate-form">
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength="6"
                className="admin-gate-input"
                placeholder="••••"
                value={passcodeInput}
                onChange={(e) => setPasscodeInput(e.target.value.replace(/\D/g, ''))}
                autoFocus
                data-testid="admin-gate-input"
              />
              {passError && <p className="admin-gate-error" data-testid="admin-gate-error">{passError}</p>}
              <button type="submit" className="admin-gate-btn" data-testid="admin-gate-submit" disabled={loggingIn}>
                {loggingIn ? "Checking…" : "Enter →"}
              </button>
            </form>
          </div>
        </div>
      ) : (
      <>
      <header className="admin-header">
        <div>
          <h1>RSVP Dashboard</h1>
          <p className="admin-sub">Nathan & Sandra · November 7, 2026</p>
        </div>
        <div className="admin-actions">
          <div className="admin-live-indicator" data-testid="admin-last-updated" title="Dashboard auto-refreshes every 30s and on tab focus">
            <span className="live-dot" />
            {lastUpdated ? (
              (() => {
                const ago = formatAgo(nowTs - lastUpdated);
                return ago === "just now" ? "Live · just now" : `Updated ${ago} ago`;
              })()
            ) : 'Loading…'}
          </div>
          <button onClick={refreshAll} className="admin-btn-ghost" data-testid="refresh-btn">
            ↻ Refresh
          </button>
          <button onClick={() => copyNonResponders('first')} className="admin-btn-ghost" data-testid="copy-nonresponders-btn" title="Copy non-responders' first names to clipboard">
            📋 Copy Non-Responders
          </button>
          <button onClick={exportCSV} className="admin-btn-ghost" data-testid="export-csv-btn">
            ↓ CSV
          </button>
          <button onClick={exportExcel} className="admin-btn" data-testid="export-excel-btn">
            ↓ Excel (Live)
          </button>
          <button onClick={exportAndWipe} className="admin-btn-danger" data-testid="wipe-data-btn" title="Permanently delete all RSVP data (after exporting)">
            🗑️ Wipe All Data
          </button>
          <button onClick={handleLogout} className="admin-btn-ghost admin-logout" data-testid="admin-logout-btn" title="Lock the dashboard">
            🔒 Lock
          </button>
        </div>
      </header>

      <div className="admin-stats">
        <div className="stat-card stat-invited">
          <div className="stat-num">{inviteStats.total_invited_headcount}</div>
          <div className="stat-label">Total Invited</div>
          <div className="stat-sub">{inviteStats.total_invited_parties} parties</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{stats.totalRsvps}</div>
          <div className="stat-label">Responses</div>
        </div>
        <div className="stat-card stat-attending">
          <div className="stat-num">{stats.attending}</div>
          <div className="stat-label">Joyfully Attending</div>
          <div className="stat-sub">at least one service</div>
        </div>
        <div className="stat-card stat-ceremony" data-testid="stat-ceremony">
          <div className="stat-num">{stats.ceremony}</div>
          <div className="stat-label">Ceremony (2:00 PM)</div>
          <div className="stat-sub">headcount</div>
        </div>
        <div className="stat-card stat-cocktail" data-testid="stat-cocktail">
          <div className="stat-num">{stats.cocktail}</div>
          <div className="stat-label">Cocktail Hour (2:45 PM)</div>
          <div className="stat-sub">headcount</div>
        </div>
        <div className="stat-card stat-reception" data-testid="stat-reception">
          <div className="stat-num">{stats.reception}</div>
          <div className="stat-label">Reception</div>
          <div className="stat-sub">headcount</div>
        </div>
        <div className="stat-card stat-declining">
          <div className="stat-num">{stats.declining}</div>
          <div className="stat-label">Regretfully Decline</div>
        </div>
        <div className="stat-card stat-pending">
          <div className="stat-num">{inviteStats.pending_headcount}</div>
          <div className="stat-label">Not Responded</div>
          <div className="stat-sub stat-sub-light">{pendingParties.length} parties</div>
        </div>
      </div>

      <div className="admin-controls">
        <input
          type="text"
          placeholder="Search by name, email, or family..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="admin-search"
          data-testid="admin-search"
        />
        <div className="filter-group">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >All</button>
          <button
            className={`filter-btn ${filter === 'attending' ? 'active' : ''}`}
            onClick={() => setFilter('attending')}
            data-testid="filter-attending"
          >Attending</button>
          <button
            className={`filter-btn ${filter === 'declined' ? 'active' : ''}`}
            onClick={() => setFilter('declined')}
            data-testid="filter-declined"
          >Declined</button>
          <button
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
            data-testid="filter-pending"
          >Not Responded</button>
        </div>
      </div>

      {filter === 'pending' ? (
        // Show non-responders list
        (() => {
          const filteredPending = pendingParties.filter(p => {
            if (!search) return true;
            const q = search.toLowerCase();
            const members = (p.members || []).join(' ').toLowerCase();
            return (
              (p.primary_guest || '').toLowerCase().includes(q) ||
              (p.first_name || '').toLowerCase().includes(q) ||
              (p.family || '').toLowerCase().includes(q) ||
              members.includes(q)
            );
          });
          if (filteredPending.length === 0) {
            return (
              <div className="admin-empty">
                {pendingParties.length === 0
                  ? "🎉 Everyone has RSVP'd!"
                  : "No pending parties match your search."}
              </div>
            );
          }
          // Group by family
          const groups = {};
          for (const p of filteredPending) {
            const key = p.family || 'Other';
            (groups[key] = groups[key] || []).push(p);
          }
          // Total individuals in filtered pending list (incl plus-ones + kids)
          const filteredHeadcount = filteredPending.reduce(
            (sum, p) => sum + (p.party_size || (p.members || []).length || 1),
            0
          );
          return (
            <div className="rsvp-table pending-list" data-testid="pending-list">
              <div className="pending-summary" data-testid="pending-summary">
                <span className="pending-summary-num">{filteredHeadcount}</span>
                <span className="pending-summary-label">
                  individual{filteredHeadcount === 1 ? '' : 's'} have not responded
                  <span className="pending-summary-sub"> · {filteredPending.length} part{filteredPending.length === 1 ? 'y' : 'ies'}</span>
                </span>
              </div>
              {Object.entries(groups).map(([fam, parties]) => {
                const familyHeadcount = parties.reduce((sum, p) => sum + (p.party_size || (p.members || []).length || 1), 0);
                return (
                <div key={fam} className="pending-group">
                  <h3 className="pending-group-title">
                    {fam}
                    <span className="pending-group-count">{parties.length} parties · {familyHeadcount} guests</span>
                  </h3>
                  <div className="pending-items">
                    {parties.map(p => {
                      const draft = phoneDrafts[p.id];
                      const currentPhone = draft !== undefined ? draft : (p.phone || '');
                      const isDirty = draft !== undefined && draft !== (p.phone || '');
                      const hasPhone = (p.phone || '').replace(/[^0-9+]/g, '').length >= 7;
                      return (
                      <div key={p.id} className="pending-item" data-testid={`pending-${p.id}`}>
                        <div className="pending-primary">{p.primary_guest}</div>
                        {(p.members || []).length > 1 && (
                          <div className="pending-members">
                            {(p.members || []).map((m, i) => (
                              <span key={i} className="pending-member-chip">{m}</span>
                            ))}
                          </div>
                        )}
                        <div className="pending-sms-row">
                          <input
                            type="tel"
                            className="pending-phone-input"
                            placeholder="Add phone e.g. +1 555 123 4567"
                            value={currentPhone}
                            onChange={(e) => setPhoneDrafts(prev => ({ ...prev, [p.id]: e.target.value }))}
                            data-testid={`phone-input-${p.id}`}
                          />
                          {isDirty && (
                            <button
                              type="button"
                              className="pending-phone-save"
                              disabled={phoneSaving[p.id]}
                              onClick={() => savePhone(p.id, currentPhone.trim())}
                              data-testid={`phone-save-${p.id}`}
                            >
                              {phoneSaving[p.id] ? 'Saving…' : 'Save'}
                            </button>
                          )}
                          <a
                            href={hasPhone ? smsHref(p.phone, p) : undefined}
                            className={`pending-sms-btn ${hasPhone ? '' : 'disabled'}`}
                            aria-disabled={!hasPhone}
                            onClick={(e) => { if (!hasPhone) e.preventDefault(); }}
                            data-testid={`sms-btn-${p.id}`}
                            title={hasPhone ? 'Open Messages app with reminder pre-filled' : 'Add & save a phone number to enable'}
                          >
                            📱 Text
                          </a>
                        </div>
                      </div>
                    );})}
                  </div>
                </div>
              );})}
            </div>
          );
        })()
      ) : loading ? (
        <div className="admin-empty">Loading RSVPs…</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty">
          {submissions.length === 0 
            ? "No RSVPs yet. Share your link to start receiving responses!"
            : "No RSVPs match your search."}
        </div>
      ) : (
        <div className="rsvp-table">
          {filtered.map((sub) => {
            const guests = sub.guests || [];
            const attending = guests.filter(g => g.attending);
            const declining = guests.filter(g => !g.attending);
            const isOpen = expandedId === sub.id;
            const updateHistory = sub.update_history || [];
            const hasBeenUpdated = updateHistory.length > 0;
            
            return (
              <div key={sub.id} className={`rsvp-card-admin ${isOpen ? 'open' : ''}`}>
                <div className="rsvp-card-top" onClick={() => setExpandedId(isOpen ? null : sub.id)}>
                  <div className="rsvp-card-name">
                    <strong>{sub.primary_guest}</strong>
                    <span className="rsvp-card-family">{sub.family}</span>
                  </div>
                  <div className="rsvp-card-counts">
                    {attending.length > 0 && (
                      <span className="pill pill-attending">+{attending.length} attending</span>
                    )}
                    {declining.length > 0 && (
                      <span className="pill pill-declining">{declining.length} declining</span>
                    )}
                    {hasBeenUpdated && (
                      <span className="pill pill-updated" title={`Updated ${updateHistory.length} time(s)`}>↻ Updated ×{updateHistory.length}</span>
                    )}
                  </div>
                  <div className="rsvp-card-meta">
                    <span className="rsvp-date">{formatDate(sub.updated_at || sub.submitted_at)}</span>
                    <span className="expand-icon">{isOpen ? '−' : '+'}</span>
                  </div>
                </div>
                
                {isOpen && (
                  <div className="rsvp-card-detail">
                    <div className="detail-row">
                      <span className="detail-label">Email</span>
                      <a href={`mailto:${sub.email}`} className="detail-value">{sub.email}</a>
                    </div>
                    
                    <div className="detail-row">
                      <span className="detail-label">Guests</span>
                      <div className="guest-pills guest-pills-dual">
                        {guests.map((g, idx) => {
                          const cer = attendsCeremony(g);
                          const coc = attendsCocktail(g);
                          const rec = attendsReception(g);
                          return (
                            <div key={idx} className="guest-pill-dual" data-testid={`admin-guest-${idx}`}>
                              <span className="guest-pill-name">{g.name}</span>
                              <span className={`svc-pill ${cer ? 'yes' : 'no'}`} title="Ceremony">
                                {cer ? '✓' : '✗'} Ceremony
                              </span>
                              <span className={`svc-pill ${coc ? 'yes' : 'no'}`} title="Cocktail Hour">
                                {coc ? '✓' : '✗'} Cocktail
                              </span>
                              <span className={`svc-pill ${rec ? 'yes' : 'no'}`} title="Reception">
                                {rec ? '✓' : '✗'} Reception
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {sub.dietary_notes && (
                      <div className="detail-row">
                        <span className="detail-label">Dietary</span>
                        <span className="detail-value">{sub.dietary_notes}</span>
                      </div>
                    )}
                    {sub.song_request && (
                      <div className="detail-row">
                        <span className="detail-label">Song</span>
                        <span className="detail-value">{sub.song_request}</span>
                      </div>
                    )}
                    {sub.message && (
                      <div className="detail-row">
                        <span className="detail-label">Message</span>
                        <span className="detail-value detail-message">"{sub.message}"</span>
                      </div>
                    )}

                    {updateHistory.length > 0 && (
                      <div className="detail-row">
                        <span className="detail-label">History</span>
                        <div className="detail-value">
                          <div className="update-history" data-testid={`update-history-${sub.id}`}>
                            <div className="history-item history-item-original">
                              <span className="history-when">{formatDate(sub.original_submitted_at)}</span>
                              <span className="history-label">First submitted</span>
                            </div>
                            {updateHistory.map((u, i) => (
                              <div key={i} className="history-item">
                                <span className="history-when">{formatDate(u.updated_at)}</span>
                                <span className="history-label">Updated</span>
                                {u.changes && Object.keys(u.changes).length > 0 && (
                                  <div className="history-changes">
                                    {Object.entries(u.changes).map(([field, val]) => {
                                      if (field === 'Attendance' && Array.isArray(val)) {
                                        return val.map((line, idx) => (
                                          <div key={`a-${idx}`} className="history-change-line">• {line}</div>
                                        ));
                                      }
                                      if (val && typeof val === 'object' && 'from' in val) {
                                        return (
                                          <div key={field} className="history-change-line">
                                            • <strong>{field}:</strong> <span className="change-old">{val.from}</span> → <span className="change-new">{val.to}</span>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="admin-footer">
        <p>Showing {filtered.length} of {total} responses</p>
        <a href="/" className="back-link">← Back to wedding site</a>
      </div>
      </>
      )}
    </div>
  );
}
